import axios from "axios";
// import { convertProductApiResponse } from "./convertProductApiResponse";
import { unmarshall } from "@aws-sdk/util-dynamodb";

export const DEBUG = true;

export const API_URL = "https://api.curagenesis.com/shared";

/**
 * Convert raw product DynamoDB response into frontend-friendly format.
 * @param {Object} item - Raw product item
 * @returns {Object}
 */
export const convertProductApiResponse = (item) => {
  const parsed = unmarshall(item);

  return {
    productId: parsed.id,
    productName: parsed.name,
    qCode: parsed.qCode,
    sizes: parsed.sizes || [],
    partNumbers: parsed.partNumbers || {},
    price: parsed.price,
    info: parsed.info,
    description: parsed.description,
    ifuUrl: parsed.ifuUrl,
    imageUrl: parsed.imageUrl,
    tags: parsed.tags || [],
    mue: parsed.mue,
    ptype: parsed.ptype,
    isVisible: parsed.isVisible,
    coverage: parsed.coverage,
    historicalPrices: parsed.historicalPrices,
    category: parsed.category
  };
};

  const parseJSONField = (value, fallback = {}) => {
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        return parsed ?? fallback;
      } catch {
        return fallback;
      }
    }
    return value ?? fallback;
  };

/**
 * Fetch the Purchase Agreement (PA) status.
 * @param {string} token - The access token for authentication.
 * @returns {Promise<boolean>} - Returns true if `paSigned` is true, otherwise false.
 */
export const getPaStatus = async (token) => {
  try {
    const response = await axios.get(
      `${API_URL}/getPaStatus`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data.paSigned === true;
  } catch (error) {
    if (DEBUG) console.error("❌ Error fetching PA status:", error);
    throw new Error("Failed to fetch PA status.");
  }
};

/**
 * Fetch the Business Associate Agreement (BAA) status.
 * @param {string} token - The access token for authentication.
 * @returns {Promise<boolean>} - Returns true if `baaSigned` is true, otherwise false.
 */
export const getBaaStatus = async (token) => {
  try {
    const response = await axios.get(
      `${API_URL}/getBaaStatus`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    // Return the value of `baaSigned` from the response body
    return response.data.baaSigned === true;
  } catch (error) {
    if (DEBUG) console.error("❌ Error fetching BAA status:", error);
    throw new Error("Failed to fetch BAA status.");
  }
};

/**
 * Fetch the embedded signing URL for the Purchase Agreement.
 * @param {string} token - The access token for authentication.
 * @returns {Promise<string>} - Returns the signing URL.
 */
export const getEmbeddedSigningUrl = async (token) => {
    try {
      const response = await axios.post(
        `${API_URL}/sendPAForSignatureEmbeddedSigning`,
        {}, // Empty body
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
  
      if (response.data.success) {
        return response.data.signingUrl;
      } else {
        throw new Error("Failed to generate signing link.");
      }
    } catch (error) {
      if (DEBUG) console.error("❌ Error fetching embedded signing URL:", error);
      throw new Error("Failed to fetch embedded signing URL.");
    }
  };

  /**
 * Fetch the recent IVR statuses.
 * @param {string} token - The access token for authentication.
 * @returns {Promise<Array>} - Returns an array of sorted IVR statuses.
 */
export const getIvrStatus = async (token) => {
    try {
      const response = await axios.post(
        `${API_URL}/getIvrStatus`,
        { getAll: true }, // Request body
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      // Process and sort the IVR data
      const sortedIVRs = (response.data.ivrs || [])
        .map((ivr) => ({
          ivrId: ivr.ivrId.S,
          status: ivr.status.S,
          createdAt: ivr.createdAt.S,
          updatedAt: ivr.updatedAt.S,
        }))
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)); // Sort by createdAt in descending order
  
      return sortedIVRs;
    } catch (error) {
      if (DEBUG) console.error("❌ Error fetching IVR statuses:", error);
      throw new Error("Failed to fetch IVR statuses.");
    }
  };

  /**
 * Fetch the embedded signing URL for a document (e.g., BAA or PA).
 * @param {string} token - The access token for authentication.
 * @param {string} documentType - The type of document (e.g., "baa", "pa").
 * @returns {Promise<string>} - Returns the signing URL.
 */
export const getEmbeddedSigningUrlForDocument = async (token, documentType) => {
    try {
      const response = await axios.post(
        `${API_URL}/createEmbeddedSigning`, // API endpoint for embedded signing
        { documentType }, // Request body with document type
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
  
      if (response.data.success) {
        return response.data.signingUrl; // Return the signing URL if successful
      } else {
        throw new Error("Failed to generate signing link.");
      }
    } catch (error) {
      if (DEBUG) console.error(`❌ Error fetching embedded signing URL for ${documentType}:`, error);
      throw new Error(`Failed to fetch embedded signing URL for ${documentType}.`);
    }
  };

  /**
 * Update the user's name.
 * @param {string} token - The access token for authentication.
 * @param {string} firstName - The user's first name.
 * @param {string} lastName - The user's last name.
 * @returns {Promise<Object>} - Returns the response data from the API.
 */
  export const updateUserName = async (token, firstName, lastName) => {
    try {
      const response = await axios.post(
        `${API_URL}/updateUserName`,
        { firstName, lastName },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
  
      if (response.status === 200) {
        return response.data; // Success
      } else {
        throw new Error("Unexpected response code");
      }
    } catch (error) {
      if (DEBUG) console.error("❌ Error updating user name:", error);
      throw new Error("Failed to update user name.");
    }
  };
  
  /**
 * Submit the contact form.
 * @param {Object} formData - The contact form data.
 * @param {string} formData.name
 * @param {string} formData.company
 * @param {string} formData.phone
 * @param {string} formData.email
 * @param {string} formData.message
 * @param {string} formData.role
 * @param {string} formData.inquiry
 * @param {string} formData.token - The reCAPTCHA token
 * @returns {Promise<Object>} - Response from the API
 */
export const submitContactForm = async (formData) => {
    try {
      const response = await axios.post(`${API_URL}/submitContactForm`, formData, {
        headers: {
          "Content-Type": "application/json",
        },
      });
  
      return response.data;
    } catch (error) {
      if (DEBUG) console.error("❌ Error submitting contact form:", error);
      throw new Error(error.response?.data?.message || "Failed to submit contact form.");
    }
  };
  
  /**
 * Fetch the list of products.
 * @param {string} token - The access token for authentication.
 * @returns {Promise<Array>} - Returns an array of parsed products.
 */
export const getProducts = async (token) => {
  try {
    const response = await axios.get(`${API_URL}/getProduct`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    // Assuming `convertProductApiResponse` is a utility function to parse the API response
    const parsedProducts = convertProductApiResponse(response.data);
    return parsedProducts;
  } catch (error) {
    if (DEBUG) console.error("❌ Failed to fetch products:", error);
    throw new Error("Failed to fetch products.");
  }
};

/**
 * Fetch a product by its name.
 * @param {string} name - The name of the product.
 * @returns {Promise<Object>} - Returns the product object.
 */
export const getProductByName = async (name) => {
  try {
    const response = await axios.get(`${API_URL}/getProduct`, {
      params: { name },
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.data.product) {
      return response.data.product; // Return the product object
    } else {
      throw new Error("Product not found.");
    }
  } catch (error) {
    console.error("❌ Failed to fetch product by name:", error);
    throw new Error("Failed to fetch product.");
  }
};

/**
 * Normalize DynamoDB JSON format to a flat object.
 * @param {Object} dynamoItem - The raw DynamoDB item.
 * @returns {Object} - The normalized object.
 */
const normalizeDynamoDBItem = (dynamoItem) => {
  const normalizedItem = unmarshall(dynamoItem);
  // for (const key in dynamoItem) {
  //   const value = dynamoItem[key];
  //   if (value?.S !== undefined) {
  //     normalizedItem[key] = value.S; // Extract string value
  //   } else if (value?.N !== undefined) {
  //     normalizedItem[key] = Number(value.N); // Extract numeric value
  //   } else if (value?.BOOL !== undefined) {
  //     normalizedItem[key] = value.BOOL; // Extract boolean value
  //   } else if (value?.L !== undefined) {
  //     // Handle list values
  //     normalizedItem[key] = value.L.map((listItem) => normalizeDynamoDBItem(listItem)); // Recursively normalize each item in the list
  //   } else {
  //     normalizedItem[key] = value; // Fallback for other types
  //   }
  // }
  return normalizedItem;
};

/**
 * Fetch a product by its Q Code.
 * @param {string} qCode - The Q Code of the product.
 * @returns {Promise<Object>} - Returns the normalized product object.
 */
export const getProductByQCode = async (qCode) => {
  try {
    const response = await axios.get(`${API_URL}/getProduct`, {
      params: { qCode },
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.data.product) {
      return normalizeDynamoDBItem(response.data.product); // Normalize the product object
    } else {
      throw new Error("Product not found.");
    }
  } catch (error) {
    console.error("❌ Failed to fetch product by Q Code:", error);
    throw new Error("Failed to fetch product.");
  }
};

/**
 * Submit the user's response to the mimedxUseCheck endpoint.
 * @param {string} token - The access token for authentication.
 * @param {string} selection - The user's selection ("Yes" or "No").
 * @returns {Promise<void>}
 */
export const mimedxUseCheck = async (token, selection) => {
  try {
    const response = await axios.post(
      `${API_URL}/mimedxUseCheck`,
      { selection },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.data.success) {
      throw new Error("Failed to submit response.");
    }
  } catch (error) {
    if (DEBUG) console.error("❌ Error submitting mimedxUseCheck response:", error);
    throw new Error("Failed to submit response.");
  }
};

/**
 * Fetch a single IVR by userId and ivrId.
 * @param {string} userId
 * @param {string} ivrId
 * @returns {Promise<Object>}
 * used to use https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getSingleIvrById - will again, temporary change
 */
export const getSingleIvrById = async (userId, ivrId) => {
  try {
    const res = await axios.post("https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getSingleIvrById", {
      userId,
      ivrId
    });

    return JSON.parse(res.data.body);
  } catch (error) {
    if (DEBUG) console.error("❌ Error fetching IVR by ID:", error);
    throw new Error("Failed to fetch IVR data.");
  }
};

/**
 * Get shipping addresses associated with a user.
 * @param {string} userId
 * @returns {Promise<Object[]>}
 */
export const getShippingAddressesForUser = async (userId) => {
  try {
    const res = await axios.post("https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getShippingAddresses", {
      userId
    });

    const parsed = JSON.parse(res.data.body);
    return Array.isArray(parsed.addresses) ? parsed.addresses : [];
  } catch (error) {
    if (DEBUG) console.error("❌ Error fetching shipping addresses:", error);
    throw new Error("Failed to load shipping addresses.");
  }
};

/**
 * Fetch list of all graft products.
 * @returns {Promise<Object[]>}
 */
export const getProductList = async (token) => {
  try {
    const res = await axios.get(
      // "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getProductList",
      "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getProductList",
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    return res.data.products.map(convertProductApiResponse);
  } catch (error) {
    if (DEBUG) console.error("❌ Error fetching product list:", error);
    throw new Error("Failed to load product list.");
  }
};

/**
 * Create an order on behalf of a user.
 * @param {Object} payload
 * @returns {Promise<Object>}
 */
export const adminCreateOrderForUser = async (payload, token) => {
  try {
    const res = await axios.post(
      "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/createMedicalOrder",
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    // console.log("🧪 Raw Lambda result:", res.data);
    return res.data;
  } catch (error) {
    if (DEBUG) console.error("❌ Error creating order:", error);
    throw new Error("Order creation failed.");
  }
};

export const getBaaDataForUser = async (userId, token) => {
  const res = await axios.post(
    "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getBaaData",
    { userId },
    { headers: { Authorization: `Bearer ${token}` } }
  );

  const raw = res.data.baaData?.[0] || {};
  // Helper to coerce common id-like fields to strings (so numbers don't break form inputs)
  const str = (v) => (v == null ? "" : `${v}`);

  const normalizeAddress = (a = {}) => {
    if (!a || typeof a !== "object") return {};
    return {
      street: a.street || a.address || a.address_line1 || a.address1 || "",
      city: a.city || "",
      state: a.state || "",
      zip: a.zip != null ? str(a.zip) : (a.postalCode != null ? str(a.postalCode) : ""),
      phone: a.phone != null ? str(a.phone) : (a.phoneNumber != null ? str(a.phoneNumber) : ""),
      fax: a.fax != null ? str(a.fax) : "",
      npi: a.npi != null ? str(a.npi) : (a.facilityNPI != null ? str(a.facilityNPI) : ""),
      tin: a.tin != null ? str(a.tin) : (a.facilityTIN != null ? str(a.facilityTIN) : ""),
      ptan: a.ptan != null ? str(a.ptan) : (a.facilityPTAN != null ? str(a.facilityPTAN) : ""),
      name: a.name || a.facilityName || a.facility || "",
    };
  };

  const normalizePhysician = (p = {}) => {
    if (!p || typeof p !== "object") return {};
    const first = p.physicianFirstName || p.firstName || "";
    const last = p.physicianLastName || p.lastName || "";
    return {
      physicianFirstName: first,
      physicianLastName: last,
      firstName: first,
      lastName: last,
      name: `${first} ${last}`.trim() || p.name || p.patientName || "",
      phone: p.phone != null ? str(p.phone) : "",
      fax: p.fax != null ? str(p.fax) : "",
      contactName: p.contactName || p.contact || "",
      contactPhone: p.contactPhone != null ? str(p.contactPhone) : "",
      npi: p.npi != null ? str(p.npi) : "",
      tin: p.tin != null ? str(p.tin) : "",
      ptan: p.ptan != null ? str(p.ptan) : "",
      specialty: p.specialty || "",
      siteName: p.siteName || "",
      street: p.street || "",
      city: p.city || "",
      state: p.state || "",
      zip: p.zip != null ? str(p.zip) : "",
    };
  };

  // Parse potentially-stringified JSON fields
  const parsedFacilityAddress = parseJSONField(raw.facilityAddress, {});
  const parsedPhysicianInfo = parseJSONField(raw.physicianInfo, {});
  const parsedShipping = parseJSONField(raw.shippingAddresses, []);
  const parsedAdditional = parseJSONField(raw.additionalPhysicians, []);

  // Normalize arrays (ensure they are arrays of objects)
  const shippingAddresses = Array.isArray(parsedShipping)
    ? parsedShipping.map((a) => normalizeAddress(typeof a === "string" ? parseJSONField(a, {}) : a))
    : [];

  const additionalPhysicians = Array.isArray(parsedAdditional)
    ? parsedAdditional.map((p) => normalizePhysician(typeof p === "string" ? parseJSONField(p, {}) : p))
    : [];

  const physicianInfo = normalizePhysician(parsedPhysicianInfo || {});

  const facilityAddress = normalizeAddress(parsedFacilityAddress || {});

  // Provide a convenience `facility` object (common place in UI code)
  const facility = {
    name: raw.facilityName || raw.facility || facilityAddress.name || "",
    phone: raw.facilityPhone || facilityAddress.phone || str(raw.facilityPhone || raw.facility_phone || ""),
    fax: raw.facilityFax || facilityAddress.fax || "",
    npi: facilityAddress.npi || str(raw.facilityNPI || raw.facilityNpi || ""),
    tin: facilityAddress.tin || str(raw.facilityTIN || ""),
    ptan: facilityAddress.ptan || str(raw.facilityPTAN || ""),
    street: facilityAddress.street || "",
    city: facilityAddress.city || "",
    state: facilityAddress.state || "",
    zip: facilityAddress.zip || "",
  };

  // Ensure facilityAddress object also contains these useful fallbacks so UI that
  // consumes facilityAddress directly (older codepaths) gets the name/phone/npi etc.
  facilityAddress.name = facilityAddress.name || raw.facilityName || raw.facility || "";
  facilityAddress.phone = facilityAddress.phone || str(raw.facilityPhone || raw.facility_phone || "");
  facilityAddress.fax = facilityAddress.fax || raw.facilityFax || "";
  facilityAddress.npi = facilityAddress.npi || str(raw.facilityNPI || raw.facilityNpi || "");
  facilityAddress.tin = facilityAddress.tin || str(raw.facilityTIN || "");
  facilityAddress.ptan = facilityAddress.ptan || str(raw.facilityPTAN || "");

  // console log what we will send the caller function

  console.log("Sending payload:", {
    ...raw,
    facilityAddress: facilityAddress,
    physicianInfo: physicianInfo,
    shippingAddresses: shippingAddresses,
    additionalPhysicians: additionalPhysicians,
    facility,
  });

  return {
    ...raw,
    // keep original parsed fields for backward-compatibility
    facilityAddress: facilityAddress,
    physicianInfo: physicianInfo,
    shippingAddresses: shippingAddresses,
    additionalPhysicians: additionalPhysicians,
    // convenience
    facility,
  };
};

export const adminUpdateOrderForUser = async (payload, token) => {
  const res = await fetch("https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/updateOrder", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    return { success: false, error: `HTTP ${res.status}` };
  }

  const json = await res.json();
  return json;
};

export const markInvoiceState = async (token, invoiceId, state, orderId = null) => {
  const res = await fetch("https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/markInvoicePaid", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ invoiceId, orderId, state }), // state is boolean: true = paid, false = unpaid
  });

  const json = await res.json();
  if (!res.ok || !json.success) {
    throw new Error(json.message || "Failed to update invoice.");
  }
  return json;
};

// utils/commonApiCalls.js
export const syncInvoiceWithOrder = async ({ invoiceId, orderId, userId }, token) => {
  const res = await fetch("https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/editInvoice", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      action: "sync",
      invoiceId,
      orderId,
      userId,
    }),
  });

  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || "Failed to sync invoice.");
  }
  return data;
};

// ===== Product normalizer (safe on both DDB-shaped and already-plain JSON) =====
const fromAttr = (v) => {
  if (!v || typeof v !== "object") return v;
  if ("S" in v) return v.S;
  if ("N" in v) return Number(v.N);
  if ("BOOL" in v) return !!v.BOOL;
  if ("L" in v) return v.L.map(fromAttr);
  if ("M" in v) {
    const out = {};
    for (const [k, val] of Object.entries(v.M)) out[k] = fromAttr(val);
    return out;
  }
  return v;
};

const normalizePartNumbers = (pn) => {
  if (!pn) return {};
  // Handles both already-normalized { "2x4": "VG-024" } and messy nested objects
  const flat = {};
  for (const [size, val] of Object.entries(pn)) {
    if (typeof val === "string") {
      flat[size] = val;
    } else if (val && typeof val === "object") {
      // Dive to find the first string (covers {S:""}, {M:{S:{S:""}}}, etc.)
      const deep =
        val.S ??
        val.value ??
        (val.M?.S ?? val.M?.value) ??
        Object.values(val).find((x) => typeof x === "string");
      flat[size] = typeof deep === "string" ? deep : "";
    } else {
      flat[size] = "";
    }
  }
  return flat;
};

export const normalizeProduct = (raw) => {
  // If it looks like AttributeValue, convert; else assume already plain.
  const looksDdb =
    raw &&
    (raw.S !== undefined ||
      raw.N !== undefined ||
      raw.BOOL !== undefined ||
      raw.L !== undefined ||
      raw.M !== undefined ||
      // Heuristic: keys like { name: {S:""} }
      Object.values(raw).some((v) => v && typeof v === "object" && ("S" in v || "N" in v || "M" in v || "L" in v)));

  const base = looksDdb ? fromAttr(raw) : raw || {};

  const toStrArr = (v) => (Array.isArray(v) ? v.map(String) : v ? [String(v)] : []);

  return {
    id: String(base.id ?? ""),
    name: String(base.name ?? ""),
    qCode: String(base.qCode ?? ""),
    description: base.description ?? "",
    info: base.info ?? "",
    imageUrl: base.imageUrl ?? "",
    ifuUrl: base.ifuUrl ?? "",
    category: base.category ?? "",
    ptype: base.ptype ?? "",
    isVisible: Boolean(base.isVisible),
    price: Number(base.price ?? 0),
    mue: Number(base.mue ?? 0),
    coverage: toStrArr(base.coverage),
    sizes: toStrArr(base.sizes),
    tags: toStrArr(base.tags),
    partNumbers: normalizePartNumbers(base.partNumbers || {}),
    historicalPrices: base.historicalPrices || {},
  };
};

export const normalizeProductsArray = (arr = []) => arr.map(normalizeProduct);
// ===== New: getProductListClean =====
/**
 * Fetch list of all graft products (always returns plain JSON products).
 * - Honors DynamoDB strong read via `strong=1`
 * - Can request backend unmarshalling via `um=true`
 * - Adds a cache-buster by default to avoid stale CDN responses after writes
 *
 * @param {string} token - Cognito/JWT access token (without "Bearer " prefix)
 * @param {Object} [opts]
 * @param {boolean} [opts.um=true]       - Ask backend to unmarshall (if it supports it)
 * @param {boolean} [opts.strong=false]  - Force strong read from DynamoDB
 * @param {boolean} [opts.cacheBust=true]- Append cb=timestamp to bypass caches
 * @returns {Promise<Array>} Plain array of product objects
 */
export const getProductListClean = async (
  token,
  { um = true, strong = false, cacheBust = true } = {}
) => {
  try {
    const baseUrl = "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getProductList";

    const qs = new URLSearchParams();
    qs.set("um", um ? "true" : "false");
    if (strong) qs.set("strong", "1");
    if (cacheBust) qs.set("cb", String(Date.now()));

    const url = `${baseUrl}?${qs.toString()}`;

    const res = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    const data = res?.data;

    // Accept both { products: [...] } and plain arrays
    const rawProducts = Array.isArray(data)
      ? data
      : Array.isArray(data?.products)
      ? data.products
      : [];

    // If backend already normalized, this is a cheap pass-through.
    // If not, this converts DDB shapes -> plain.
    return normalizeProductsArray(rawProducts);
  } catch (error) {
    if (DEBUG) {
      // Prefer readable backend error body if present
      const msg =
        error?.response?.data?.message ||
        error?.response?.data ||
        error?.message ||
        "Unknown error";
      console.error("❌ Error fetching product list:", msg, error?.response);
    }
    throw new Error("Failed to load product list.");
  }
};


/**
 * Upsert a product (create or update). Expects a plain JSON product object.
 * This calls a placeholder endpoint that should implement the PartiQL update shown in the UI.
 */
/** Send product fields at the root (NOT wrapped in { product: ... }) */
export async function upsertProduct(product, token) {
  const res = await fetch(`https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/upsertProduct`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(product),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.message || `upsertProduct failed: ${res.status}`);
  return data;
}

export async function deleteProduct(idOrQCode, token) {
  const res = await fetch(`https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/deleteProduct`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ id: idOrQCode }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.message || `deleteProduct failed: ${res.status}`);
  return data;
}

export const adminGetUsersList = async (token) => {
  const res = await axios.post(
    `https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getUser`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  // Expecting array of objects with at least: UserId, email, FirstName, LastName
  return Array.isArray(res.data?.users) ? res.data.users : [];
};
